import React, { useState, useEffect } from 'react';
import { Input, Button, message } from 'antd';
import { DeleteOutlined, PlusOutlined, CheckOutlined } from '@ant-design/icons';
import instance from '../../axios';
import { getUserToken } from '../../utils/userAuthenticate';
import { formatStoreCurrency } from '../../assets/currency';
import BarLoading from '../../components/Loaders/BarLoading';
import { BackButtonIcon } from "@/components/Icons" ;
import "./styles/PhysicalGcFulfillTable.css";

const PhysicalGCFulfill = ({ orderId, orderData: initialOrderData, onBack }) => {
  const [orderData, setOrderData] = useState(initialOrderData || null);
  const [isLoading, setIsLoading] = useState(!initialOrderData);
  const [fulfilledQuantity, setFulfilledQuantity] = useState('1');
  const [cardNumbers, setCardNumbers] = useState(['']);
  const [validatedCardIndices, setValidatedCardIndices] = useState(new Set());
  const [validatingCardIndex, setValidatingCardIndex] = useState(null);
  const [trackingUrl, setTrackingUrl] = useState('');
  const [awbNumber, setAwbNumber] = useState('');
  const [isFulfilling, setIsFulfilling] = useState(false);
  const [fulfilledCardsFromAPI, setFulfilledCardsFromAPI] = useState([]);

  useEffect(() => {
    if (orderId && !initialOrderData) {
      fetchOrderDetails();
    } else if (initialOrderData) {
      initializeOrderData(initialOrderData);
    }
  }, [orderId, initialOrderData]);

  const initializeOrderData = (data) => {
    setOrderData(data);
    const totalQuantity = data?.quantity || 1;
    
    // Store the fulfilled cards from API response
    const fulfilledCards = data?.cards || [];
    setFulfilledCardsFromAPI(fulfilledCards);
    
    const initialCardNumbers = Array(totalQuantity).fill('');
    
    // Fill in the fulfilled card numbers
    fulfilledCards.forEach((card, idx) => {
      if (idx < totalQuantity) {
        initialCardNumbers[idx] = card;
      }
    });
    
    setCardNumbers(initialCardNumbers);
    setFulfilledQuantity(totalQuantity.toString());
    
    // Mark all fulfilled cards as validated
    if (fulfilledCards.length > 0) {
      const validatedIndices = new Set();
      fulfilledCards.forEach((_, idx) => {
        validatedIndices.add(idx);
      });
      setValidatedCardIndices(validatedIndices);
    }
  };

  const fetchOrderDetails = async () => {
    setIsLoading(true);
    const url = `/giftcard/orders?page=1&pageSize=1&isPhysical=true&orderId=${orderId}`;
    const headers = {
      Authorization: getUserToken(),
    };

    try {
      const res = await instance.post(url, {}, { headers });
      const resData = res.data;

      if (resData.code === 200) {
        const orderDetails = resData.data?.[0] || resData.data;
        initializeOrderData(orderDetails);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      message.error('Failed to load order details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardNumberChange = (index, value) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const updatedCards = [...cardNumbers];
    updatedCards[index] = numericValue;
    setCardNumbers(updatedCards);
    
    // Remove from validated if user edits the card number
    const newValidated = new Set(validatedCardIndices);
    newValidated.delete(index);
    setValidatedCardIndices(newValidated);
  };

  const handleAddCardField = () => {
    const maxQuantity = orderData?.quantity || 1;
    if (cardNumbers.length < maxQuantity) {
      setCardNumbers([...cardNumbers, '']);
    } else {
      message.warning(`Maximum ${maxQuantity} card number(s) allowed`);
    }
  };

  // Helper function to check if a card number is already validated in the current form
  const isCardNumberAlreadyValidated = (cardNumber, excludeIndex) => {
    return cardNumbers.some((card, idx) => 
      idx !== excludeIndex && 
      card.trim() === cardNumber.trim() && 
      validatedCardIndices.has(idx)
    );
  };

  // Helper function to check if a card number already exists in fulfilled cards from API
  const isCardNumberAlreadyFulfilled = (cardNumber) => {
    return fulfilledCardsFromAPI.includes(cardNumber.trim());
  };

  const handleValidateCard = async (index) => {
    const cardNumber = cardNumbers[index];
    
    if (!cardNumber.trim()) {
      message.error('Please enter a card number');
      return;
    }

    // Check if card is already fulfilled from API
    if (isCardNumberAlreadyFulfilled(cardNumber)) {
      message.error('This gift card has already been fulfilled');
      return;
    }

    // Check if card is already validated in the current form
    if (isCardNumberAlreadyValidated(cardNumber, index)) {
      message.error('This gift card number is already validated. Please use a different card number');
      return;
    }

    setValidatingCardIndex(index);

    const url = `/giftcard/validateGC`;
    const headers = {
      Authorization: getUserToken(),
    };

    const payload = {
      orderId: orderData.id,
      cardNumber: cardNumber.trim(),
    };

    try {
      const res = await instance.post(url, payload, { headers });
      const resData = res.data;

      if (resData.success || resData.code === 200) {
        const newValidated = new Set(validatedCardIndices);
        newValidated.add(index);
        setValidatedCardIndices(newValidated);
        message.success('Card number validated successfully!');
      } else {
        message.error(resData.message || 'Card validation failed');
      }
      } catch (error) {
      console.error('Error validating card number:', error);
      message.error(
        error?.response?.data?.response ||
        error?.response?.data?.message ||
        'An error occurred while validating card number'
      );
    } finally {
        setValidatingCardIndex(null);
    }
  };

  const handleFulfillGiftCard = async () => {
    const filledCardIndices = cardNumbers
      .map((card, idx) => card.trim() !== '' ? idx : -1)
      .filter(idx => idx !== -1);

    if (filledCardIndices.length === 0) {
      message.error('Please enter at least one card number');
      return;
    }

    if (filledCardIndices.some(idx => !validatedCardIndices.has(idx))) {
      message.error('Please validate all card numbers');
      return;
    }

    if (!trackingUrl.trim() && !awbNumber.trim()) {
      message.error('Please enter tracking URL or AWB number');
      return;
    }

    // Validate Tracking URL if provided
    if (trackingUrl.trim()) {
      try {
        // Add protocol if missing
        const urlToValidate = trackingUrl.includes('://') ? trackingUrl : `https://${trackingUrl}`;
        new URL(urlToValidate);
      } catch {
        message.error('Please enter a valid tracking URL');
        return;
      }
    }

    // Get filled card numbers
    const filledCards = filledCardIndices.map(idx => cardNumbers[idx]);

    // Check for duplicates in the payload - only send unique card numbers
    const uniqueCards = [...new Set(filledCards)];

    if (uniqueCards.length < filledCards.length) {
      message.warning('Duplicate card numbers detected. Only unique card numbers will be fulfilled');
    }

    // Filter out already fulfilled cards from API
    const newCardsToFulfill = uniqueCards.filter(
      card => !fulfilledCardsFromAPI.includes(card.trim())
    );

    // If all cards are already fulfilled, show message
    if (newCardsToFulfill.length === 0) {
      message.warning('All selected cards have already been fulfilled');
      return;
    }

    setIsFulfilling(true);
    const url = `/giftcard/fulfillPhysicalGc`;
    const headers = {
      Authorization: getUserToken(),
    };

    const payload = {
      cardNumbers: newCardsToFulfill,
      orderId: orderData.id,
      quantity: newCardsToFulfill.length
    };

    // Only include tracking fields if they have values
    if (trackingUrl.trim()) {
      payload.tracking_url = trackingUrl;
    }
    if (awbNumber.trim()) {
      payload.tracking_number = awbNumber;
    }

    try {
      const res = await instance.post(url, payload, { headers });
      const resData = res.data;

      if (resData.success || resData.code === 200) {
        message.success('Gift card fulfilled successfully!');
        if (onBack) {
          onBack();
        }
      } else {
        message.error(resData.message || 'Failed to fulfill gift card');
      }
    } catch (error) {
      console.error('Error fulfilling gift card:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred while fulfilling the gift card';
      message.error(errorMessage);
    } finally {
      setIsFulfilling(false);
    }
  };

  if (isLoading) {
    return <BarLoading />;
  }

  const orderValue = parseFloat(orderData?.orderValue || orderData?.order_value || 0);
  const totalOrderQuantity = orderData?.quantity || 1;
  const perCardValue = orderValue / totalOrderQuantity;
  const itemTotal = perCardValue * parseInt(fulfilledQuantity || 1, 10);
  const discount = 0;
  const tax = 0;
  const totalAmount = itemTotal - discount + tax;
  const isFulfilled = orderData?.fulfillment_status === 'fulfilled';
  const hasAnyValidatedCard = validatedCardIndices.size > 0;
  const allFilledCardsValidated = cardNumbers.every(
    (card, idx) => card.trim() === '' || validatedCardIndices.has(idx)
  ) && hasAnyValidatedCard;
  
  // Get the number of fulfilled cards from response
  const fulfilledCardsCount = orderData?.cards?.length || 0;

  return (
    <div className="physical-gc-fulfill-container component">
      <div className="component-primary-heading">
        <button 
          onClick={onBack} 
          className='back-button'
        >
          <div className="icon-container">
              <BackButtonIcon />
          </div>
           Back
        </button>
        Fulfill Physical Gift Card
      </div>

      <div className="fulfill-content">
        <div className="fulfill-left-section">
          <div className="order-info-card">
            <div className="order-number">
              Order {orderData?.id}
            </div>
            <div className="product-info">
              <div className="product-item">
                <div className="product-details">
                  <div className="product-name">Physical gift card</div>
                  <div className="product-price">
                    {formatStoreCurrency(perCardValue)}
                  </div>
                </div>

                <div className="quantity-display">
                  <Input
                    type="number"
                    value={fulfilledQuantity}
                    className="quantity-input"
                    disabled={true}
                    min="1"
                    max={totalOrderQuantity}
                  />
                  <span className="quantity-separator">|</span>
                  <span className="quantity-max">{totalOrderQuantity}</span>
                </div>

                <div className="item-total">
                  {formatStoreCurrency(itemTotal)}
                </div>
              </div>
            </div>
          </div>

          <div className="card-validation-section">
            <div className="validation-title">Gift Card Numbers</div>
            
            <div className="card-inputs-container">
              {cardNumbers.map((cardNumber, index) => {
                // Check if this card was fulfilled from response
                const isPrefilledCard = index < fulfilledCardsCount;
                const isCardValidated = validatedCardIndices.has(index);
                
                return (
                  <div key={index} className="card-input-group">
                    <Input
                      placeholder={`Card number ${index + 1}`}
                      value={cardNumber}
                      onChange={(e) => handleCardNumberChange(index, e.target.value)}
                      className="card-number-input"
                      disabled={isFulfilled || isPrefilledCard}
                    />
                    <Button
                      type={isCardValidated ? "primary" : "default"}
                      onClick={() => handleValidateCard(index)}
                      loading={validatingCardIndex === index}
                      disabled={isFulfilled || !cardNumber.trim()}
                      icon={isCardValidated ? <CheckOutlined /> : undefined}
                      style={{
                        minWidth: '110px',
                        backgroundColor: isCardValidated ? '#52c41a' : undefined,
                        borderColor: isCardValidated ? '#52c41a' : undefined,
                      }}
                    >
                      {isCardValidated ? 'Validated' : 'Validate'}
                    </Button>

                  </div>
                );
              })}
            </div>


          </div>
        </div>

        <div className="fulfill-right-section">
          <div className="summary-card">
            <div className="summary-title">Summary</div>

            <div className="summary-row">
              <span className="summary-label">Subtotal</span>
              <span className="summary-value">{formatStoreCurrency(itemTotal)}</span>
            </div>

            <div className="summary-row">
              <span className="summary-label">{fulfilledQuantity} item(s)</span>
            </div>

            <div className="summary-row">
              <span className="summary-label">Discount</span>
              <span className="summary-value">{formatStoreCurrency(discount)}</span>
            </div>

            <div className="summary-row">
              <span className="summary-label">Tax</span>
              <span className="summary-value">{formatStoreCurrency(tax)}</span>
            </div>

            <div className="summary-row summary-total">
              <span className="summary-label">Total</span>
              <span className="summary-value">{formatStoreCurrency(totalAmount)}</span>
            </div>

            <div className={`input-group ${!hasAnyValidatedCard ? 'disabled-section' : ''}`}>
              <Input
                placeholder="Tracking URL"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                className="fulfill-input"
                disabled={isFulfilled || !hasAnyValidatedCard}
              />
            </div>

            <div className={`input-group ${!hasAnyValidatedCard ? 'disabled-section' : ''}`}>
              <Input
                placeholder="AWB Number"
                value={awbNumber}
                onChange={(e) => setAwbNumber(e.target.value.replace(/[^0-9]/g, ''))}
                className="fulfill-input"
                disabled={isFulfilled || !hasAnyValidatedCard}
              />
            </div>

            <Button
              type="primary"
              block
              size="large"
              className="fulfill-submit-btn"
              onClick={handleFulfillGiftCard}
              loading={isFulfilling}
              disabled={isFulfilled || !allFilledCardsValidated}
            >
              {isFulfilled ? 'Fulfilled' : 'Fulfill gift card'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalGCFulfill;